import { apiClient } from './client';

/**
 * Get all courses (admin/teacher, tenant-scoped).
 * `limit` is 100 at most: a larger one is a 400. For a list that needs every course, use
 * fetchAllCourses (hooks/useAllCourses.js), which reads the pages.
 * @param {{ page?, limit?, search?, level?, category?, isPublished?, sortBy?, sortOrder? }} params
 * @returns {{ data: Array, meta: { page, limit, total, totalPages, hasNext, hasPrev } }}
 */
export async function getCoursesAdmin(params = {}) {
  const { data } = await apiClient.get('/courses/admin', { params });
  return data;
}

/**
 * Get course by ID (staff read, drafts included).
 * The public GET /courses/:id only resolves published courses, so a course that was just created
 * (or unpublished) would open as "not found" in the workspace.
 * @param {string} id
 */
export async function getCourse(id) {
  const { data } = await apiClient.get(`/courses/admin/${id}`);
  return data;
}

/**
 * Create course.
 * @param {object} body - title, description?, level?, price?, currency?, category?, tags?
 */
export async function createCourse(body) {
  const { data } = await apiClient.post('/courses', body);
  return data;
}

/**
 * Update course.
 * @param {string} id
 * @param {object} body
 */
export async function updateCourse(id, body) {
  const { data } = await apiClient.put(`/courses/${id}`, body);
  return data;
}

/**
 * Publish or unpublish a course. ADMIN only: the backend refuses a publish-state change from
 * anyone else, through these routes and through PUT /courses/:id.
 * @param {string} id
 * @param {boolean} published
 */
export async function setCoursePublished(id, published) {
  const { data } = await apiClient.post(`/courses/${id}/${published ? 'publish' : 'unpublish'}`);
  return data;
}

/**
 * Get course statistics.
 * @param {string} id
 */
export async function getCourseStats(id) {
  const { data } = await apiClient.get(`/courses/${id}/stats`);
  return data;
}

/**
 * Get course enrollments (paginated, 100 a page at most).
 * `search` matches the student's name, email or phone; `status` is active, completed or cancelled
 * (cancelled = no access: refunded, revoked or expired). Each row carries `isActive` and the
 * student's `user.phone`.
 * @param {string} courseId
 * @param {{ page?, limit?, search?, status?, sortBy?, sortOrder? }} params
 */
export async function getCourseEnrollments(courseId, params = {}) {
  const { data } = await apiClient.get(`/courses/${courseId}/enrollments`, { params });
  return data;
}

/**
 * Delete a course (admin/teacher only).
 * SECURITY: Frontend should validate permissions before calling this.
 * Backend will validate authorization on 401/403.
 * @param {string} id - Course ID
 * @returns {object} Deletion response
 */
export async function deleteCourse(id) {
  const { data } = await apiClient.delete(`/courses/${id}`);
  return data;
}
